import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { useAuth } from '../components/auth/AuthContext';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';

const functions = getFunctions();

const DEFAULT_RETENTION_DAYS = 365;
const MIN_RETENTION_DAYS = 30;

const CalendarCleanupPage = ({ user }) => {
  const { currentUser } = useAuth();
  const effectiveUser = user || currentUser;
  const isAdmin = effectiveUser?.role === 'admin';

  const [settings, setSettings] = useState({ enabled: false, retentionDays: DEFAULT_RETENTION_DAYS });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [retentionDays, setRetentionDays] = useState(String(DEFAULT_RETENTION_DAYS));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      setLoadingSettings(false);
      return;
    }

    const loadSettings = async () => {
      try {
        const [enabledSnap, daysSnap] = await Promise.all([
          getDoc(doc(db, collectionPath('systemSettings'), 'calendarCleanupEnabled')),
          getDoc(doc(db, collectionPath('systemSettings'), 'calendarEventRetentionDays'))
        ]);

        const enabledValue = enabledSnap.exists() ? enabledSnap.data().value : false;
        const daysValue = Number(daysSnap.exists() ? daysSnap.data().value : NaN);
        const resolvedDays = Number.isFinite(daysValue) && daysValue > 0
          ? Math.floor(daysValue)
          : DEFAULT_RETENTION_DAYS;

        setSettings({
          enabled: enabledValue === true || enabledValue === 'true',
          retentionDays: resolvedDays,
          enabledConfigured: enabledSnap.exists(),
          daysConfigured: daysSnap.exists()
        });
        setRetentionDays(String(resolvedDays));
      } catch (err) {
        console.error('[CalendarCleanup] Failed to load settings:', err);
        setToast({ message: `Failed to load settings: ${err.message}`, type: 'error' });
      } finally {
        setLoadingSettings(false);
      }
    };

    loadSettings();
  }, [isAdmin]);

  const runCleanup = async (dryRun) => {
    const days = Number(retentionDays);
    if (!Number.isFinite(days) || days < MIN_RETENTION_DAYS) {
      setToast({ message: `Retention must be at least ${MIN_RETENTION_DAYS} days.`, type: 'warning' });
      return;
    }

    if (!dryRun) {
      const confirmed = window.confirm(
        `PERMANENTLY DELETE calendar events that ended more than ${days} days ago?\n\n` +
        'This cannot be undone. Run a preview first if you have not already.'
      );
      if (!confirmed) return;
    }

    setRunning(true);
    setResult(null);
    try {
      const fn = httpsCallable(functions, 'triggerCalendarEventCleanup');
      const res = await fn({ dryRun, retentionDays: days });
      setResult(res.data || null);
      const count = res.data?.deleted || 0;
      setToast({
        message: dryRun
          ? `Preview complete — ${count.toLocaleString()} event(s) would be deleted.`
          : `Deleted ${count.toLocaleString()} calendar event(s).`,
        type: 'success'
      });
    } catch (err) {
      console.error('[CalendarCleanup] Run failed:', err);
      setToast({ message: `Cleanup failed: ${err.message}`, type: 'error' });
    } finally {
      setRunning(false);
    }
  };

  if (!isAdmin) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-semibold">Access Denied</p>
          <p className="text-red-600 text-sm mt-1">
            Calendar event cleanup is restricted to administrators.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow p-6 overflow-y-auto bg-gray-50">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Calendar Event Cleanup</h1>
        <p className="text-gray-600 mt-1">
          Permanently delete calendar events that ended longer ago than the retention window.
        </p>
      </div>

      {/* Automated schedule status */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Automated Cleanup</h2>
        <p className="text-sm text-gray-600 mb-4">
          Runs daily at 3:30 AM Eastern. Configure both values on the{' '}
          <a href="/admin/system-settings" className="text-indigo-600 hover:underline">System Settings</a> page.
        </p>

        {loadingSettings ? (
          <div className="text-gray-500 text-sm">Loading settings…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                calendarCleanupEnabled
              </div>
              <div className={`text-lg font-bold ${settings.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                {settings.enabled ? 'Enabled' : 'Disabled'}
              </div>
              {!settings.enabledConfigured && (
                <div className="text-xs text-amber-700 mt-1">
                  Not configured — defaults to disabled, so nothing is deleted automatically.
                </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                calendarEventRetentionDays
              </div>
              <div className="text-lg font-bold text-gray-900">
                {settings.retentionDays.toLocaleString()} days
              </div>
              {!settings.daysConfigured && (
                <div className="text-xs text-amber-700 mt-1">
                  Not configured — using the {DEFAULT_RETENTION_DAYS}-day default.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Manual run */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Run Manually</h2>
        <p className="text-sm text-gray-600 mb-4">
          Manual runs work whether or not the automated job is enabled. Always preview first.
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Delete events older than
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={MIN_RETENTION_DAYS}
                value={retentionDays}
                onChange={(e) => setRetentionDays(e.target.value)}
                className="w-32 border border-gray-300 rounded px-3 py-2"
              />
              <span className="text-sm text-gray-600">days</span>
            </div>
          </div>

          <Tooltip text="Counts what would be deleted without changing anything." position="top">
            <button
              onClick={() => runCleanup(true)}
              disabled={running}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
            >
              {running ? 'Running…' : 'Preview (Dry Run)'}
            </button>
          </Tooltip>

          <Tooltip text="Permanently deletes matching events. This cannot be undone." position="top">
            <button
              onClick={() => runCleanup(false)}
              disabled={running}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
            >
              Delete Events
            </button>
          </Tooltip>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          Events still in progress, or ending after the cutoff, are never deleted. Minimum retention is {MIN_RETENTION_DAYS} days.
        </p>
      </div>

      {/* Results */}
      {result && (
        <div className={`rounded-lg shadow p-6 ${result.dryRun ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'}`}>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {result.dryRun ? 'Preview Results' : 'Cleanup Results'}
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Scanned</div>
              <div className="text-2xl font-bold text-gray-900">{(result.scanned || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">
                {result.dryRun ? 'Would Delete' : 'Deleted'}
              </div>
              <div className="text-2xl font-bold text-red-600">{(result.deleted || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Kept</div>
              <div className="text-2xl font-bold text-gray-900">{(result.skipped || 0).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Retention</div>
              <div className="text-2xl font-bold text-gray-900">{result.retentionDays}d</div>
            </div>
          </div>

          <div className="text-sm text-gray-600 mb-3">
            Cutoff: events ending before {new Date(result.cutoffDate).toLocaleString()}
          </div>

          {result.samples?.length > 0 && (
            <div className="overflow-x-auto">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Sample ({result.samples.length} of {(result.deleted || 0).toLocaleString()})
              </div>
              <table className="min-w-full divide-y divide-gray-200 bg-white rounded">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {result.samples.map(sample => (
                    <tr key={sample.id}>
                      <td className="px-3 py-2 text-sm text-gray-900">{sample.title}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 whitespace-nowrap">
                        {sample.startTime ? new Date(sample.startTime).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{sample.userDisplayName || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </main>
  );
};

export default CalendarCleanupPage;
